import { apiClient } from "./authService";
import { IPageable } from "../types/table/interfaces.type";

interface PromoData {
  [key: string]: any;
}

export type ClientSelectItem = {
  id: string;
  name: string;
  email?: string | null;
  mobile?: string | null;
  phone?: string | null;
  country_code?: string | null;
  label?: string | null;
};

export const getClientsSelect = async (params: {
  businessId: string;
  unitId?: string | null;
  onlyWithAppointments?: boolean;
  ids?: string[] | string | null;
  /**
   * Type-ahead mode: with `search` or `limit` the API returns the closest
   * `limit` matches (default 20) plus the clients in `ids`, not the whole list.
   */
  search?: string;
  limit?: number;
}): Promise<{ content: ClientSelectItem[] }> => {
  const { businessId, unitId, onlyWithAppointments, ids, search, limit } =
    params;
  const idsParam = Array.isArray(ids) ? ids.join(",") : ids || undefined;

  const response = await apiClient.get<{ content: ClientSelectItem[] }>(
    "/clients/select",
    {
    params: {
      businessId,
      unitId: unitId || undefined,
      onlyWithAppointments:
        typeof onlyWithAppointments === "boolean"
          ? String(onlyWithAppointments)
          : undefined,
      ids: idsParam || undefined,
      search: search?.trim() || undefined,
      limit,
    },
    }
  );

  return response.data;
};

export const getAllClients = async (
  businessId: string,
  unitId?: string | null
): Promise<any> => {
  try {
    const params: any = {};

    // venue in frontend = business in backend
    if (businessId && businessId !== "all") {
      params.businessId = businessId;
    }

    // branch in frontend = unit in backend
    if (unitId) {
      params.unitId = unitId;
    }

    const response = await apiClient.get("/clients", { params });
    return response.data;
  } catch (error) {
    throw error;
  }
};


export const createClient = async (clientData: PromoData): Promise<any> => {
  try {
    const response = await apiClient.post("/clients/create", clientData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateClient = async (
  id: string,
  clientData: PromoData
): Promise<any> => {
  try {
    const response = await apiClient.patch(`/clients/update/${id}`, clientData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getClientById = async (id: string): Promise<any> => {
  try {
    const response = await apiClient.get(`/clients/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteClient = async (id: string): Promise<any> => {
  try {
    const response = await apiClient.delete(`/clients/delete/${id}`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const banClient = async (id: string): Promise<any> => {
  try {
    const response = await apiClient.post(`/clients/${id}/ban`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const unbanClient = async (id: string): Promise<any> => {
  try {
    const response = await apiClient.post(`/clients/${id}/unban`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllClientsInOrganization = async (
  businessId?: string
): Promise<any> => {
  const response = await apiClient.get("/clients/all", {
    params: businessId ? { businessId } : undefined,
  });

  return response.data;
};

export interface ClientsPageQuery {
  /** "all" or empty means every business. */
  businessId?: string | null;
  /** A branch; without one the list covers the business (or organization). */
  unitId?: string | null;
  page: number;
  size: number;
  /** Matches name, phone or email. */
  search?: string;
  /** `fullName,asc` (default), `fullName,desc`, `createdAt,desc`... */
  sort?: string;
  /** Only clients who use the mobile app ("true") or never have ("false"). */
  inApp?: "true" | "false";
}

const buildClientsListParams = ({
  businessId,
  unitId,
  search,
  sort,
  inApp,
}: Omit<ClientsPageQuery, "page" | "size">) => ({
  ...(businessId && businessId !== "all" ? { businessId } : {}),
  ...(unitId && unitId !== "all" ? { unitId } : {}),
  ...(search?.trim() ? { search: search.trim() } : {}),
  ...(sort ? { sort } : {}),
  ...(inApp ? { inApp } : {}),
});

/** One page of clients; the server does the search and the paging. */
export const getClientsPage = async (
  query: ClientsPageQuery
): Promise<IPageable<any>> => {
  const hasUnit = Boolean(query.unitId && query.unitId !== "all");
  const response = await apiClient.get<IPageable<any>>(
    hasUnit ? "/clients" : "/clients/all",
    {
      params: {
        ...buildClientsListParams(query),
        page: query.page,
        size: query.size,
      },
    }
  );
  return response.data;
};

/** Every client matching the filters, as the CSV file the server builds. */
export const downloadClientsCsv = async (
  query: Omit<ClientsPageQuery, "page" | "size">,
  onProgress?: (loaded: number, total: number) => void
): Promise<void> => {
  // The old @types/axios in this project predates onDownloadProgress, which
  // axios itself supports.
  const config: any = {
    params: buildClientsListParams(query),
    responseType: "blob",
    onDownloadProgress: (event: { loaded: number; total?: number }) => {
      if (onProgress && event.total) onProgress(event.loaded, event.total);
    },
  };
  const response = await apiClient.get<Blob>("/clients/export", config);
  const url = URL.createObjectURL(response.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `clients-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
